/**
 * Pre-built calculator scenario URLs for each guide article.
 *
 * Each URL uses the ?s= share-state format (LZ-compressed JSON) so that clicking
 * "Run this scenario yourself" in an article opens the calculator with the exact
 * inputs described in that article: two tabs, side by side, ready to compare.
 */

import type { ShareState, ShareableTab } from './shareUtils';
import { buildScenarioUrlFromState } from './shareUtils';
import type { AnnualExpensesData, ExtraPaymentsData } from '../store/calculatorStore';
import type { AffordabilityInputs } from '../types/affordabilityTypes';

// ---------------------------------------------------------------------------
// Shared defaults
// ---------------------------------------------------------------------------

const START_DATE = '2026-05-01T12:00:00.000Z';

const NO_EXTRA_PAYMENTS: ExtraPaymentsData = {
  frequency: 'monthly',
  amount: 0,
  startDate: null,
};

// Article scenarios show pure P&I (and MI where applicable); no homeowner
// expenses, so zeroing these out keeps the calculator output consistent with
// the payment figures quoted in each article.
const ZERO_EXPENSES: AnnualExpensesData = {
  propertyTaxes: { type: 'amount', value: 0 },
  homeInsurance: { type: 'amount', value: 0 },
  hoaUtilities: { type: 'amount', value: 0 },
};

// ---------------------------------------------------------------------------
// Utility: builds a relative /?s=... URL from a ShareState object.
// Unlike generateShareUrl() this does not need window.location and can run
// safely at module initialisation time (outside the React tree / browser env).
// ---------------------------------------------------------------------------

const buildScenarioUrl = buildScenarioUrlFromState;

// ---------------------------------------------------------------------------
// Article 1: Fixed vs Variable - Canada
// $600k purchase, 20% down ($480k mortgage)
// Tab A: 5.24% fixed | Tab B: 4.79% variable (VRM)
// ---------------------------------------------------------------------------

const tabA1: ShareableTab = {
  id: 'art1-tab-a',
  title: 'Fixed 5.24%',
  mortgageTerms: {
    country: 'Canada',
    homePrice: 600000,
    downPaymentAmount: 120000,
    downPaymentPercentage: 20,
    mortgageType: 'fixed',
    loanProgram: 'conventional',
    rate: 5.24,
    amortizationPeriod: 25,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0 },
    canadianTermDetails: { termLength: 5, showRenewalScenario: false },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

const tabB1: ShareableTab = {
  id: 'art1-tab-b',
  title: 'Variable 4.79%',
  mortgageTerms: {
    country: 'Canada',
    homePrice: 600000,
    downPaymentAmount: 120000,
    downPaymentPercentage: 20,
    mortgageType: 'variable',
    loanProgram: 'conventional',
    rate: 4.79,
    amortizationPeriod: 25,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0 },
    canadianTermDetails: { termLength: 5, showRenewalScenario: false },
    variableDetails: {
      primeRate: 5.45,
      discount: -0.66,
      variableType: 'vrm',
      rateChanges: [],
    },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

export const FIXED_VS_VARIABLE_CANADA_URL = buildScenarioUrl({
  version: 1,
  activeTabId: 'compare',
  tabs: [tabA1, tabB1],
});

// ---------------------------------------------------------------------------
// Article 2: Amortization vs Term - Canada
// $625k purchase, 20% down ($500k mortgage), 5.10% fixed
// Tab A: 25-year amortization | Tab B: 30-year amortization
// ---------------------------------------------------------------------------

const tabA2: ShareableTab = {
  id: 'art2-tab-a',
  title: '25-Year Amort',
  mortgageTerms: {
    country: 'Canada',
    homePrice: 625000,
    downPaymentAmount: 125000,
    downPaymentPercentage: 20,
    mortgageType: 'fixed',
    loanProgram: 'conventional',
    rate: 5.10,
    amortizationPeriod: 25,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0 },
    canadianTermDetails: { termLength: 5, showRenewalScenario: false },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

const tabB2: ShareableTab = {
  id: 'art2-tab-b',
  title: '30-Year Amort',
  mortgageTerms: {
    country: 'Canada',
    homePrice: 625000,
    downPaymentAmount: 125000,
    downPaymentPercentage: 20,
    mortgageType: 'fixed',
    loanProgram: 'conventional',
    rate: 5.10,
    amortizationPeriod: 30,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0 },
    canadianTermDetails: { termLength: 5, showRenewalScenario: false },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

export const AMORTIZATION_TERM_CANADA_URL = buildScenarioUrl({
  version: 1,
  activeTabId: 'compare',
  tabs: [tabA2, tabB2],
});

// ---------------------------------------------------------------------------
// Article 3: Fixed vs ARM - USA
// $450k purchase, 20% down ($360k loan)
// Tab A: 30yr fixed at 6.75% | Tab B: 5/1 ARM at 5.90%
// ---------------------------------------------------------------------------

const tabA3: ShareableTab = {
  id: 'art3-tab-a',
  title: '30yr Fixed 6.75%',
  mortgageTerms: {
    country: 'USA',
    homePrice: 450000,
    downPaymentAmount: 90000,
    downPaymentPercentage: 20,
    mortgageType: 'fixed',
    loanProgram: 'conventional',
    rate: 6.75,
    amortizationPeriod: 30,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0 },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

const tabB3: ShareableTab = {
  id: 'art3-tab-b',
  title: '5/1 ARM 5.90%',
  mortgageTerms: {
    country: 'USA',
    homePrice: 450000,
    downPaymentAmount: 90000,
    downPaymentPercentage: 20,
    mortgageType: 'adjustable',
    loanProgram: 'conventional',
    rate: 5.90,
    amortizationPeriod: 30,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0 },
    armDetails: {
      initialFixedPeriod: 5,
      adjustmentFrequency: 12,
      indexRate: 4.15,
      margin: 1.75,
      initialAdjustmentCap: 2,
      periodicCap: 2,
      lifetimeCap: 5,
    },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

export const FIXED_VS_ARM_USA_URL = buildScenarioUrl({
  version: 1,
  activeTabId: 'compare',
  tabs: [tabA3, tabB3],
});

// ---------------------------------------------------------------------------
// Article 4: FHA vs Conventional - USA
// $350k purchase
// Tab A: FHA, 3.5% down ($12,250), 6.50% | Tab B: Conventional, 5% down, 6.75%
// ---------------------------------------------------------------------------

const tabA4: ShareableTab = {
  id: 'art4-tab-a',
  title: 'FHA 3.5% down',
  mortgageTerms: {
    country: 'USA',
    homePrice: 350000,
    downPaymentAmount: 12250,
    downPaymentPercentage: 3.5,
    mortgageType: 'fixed',
    loanProgram: 'fha',
    rate: 6.50,
    amortizationPeriod: 30,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0 },
    fhaDetails: {
      upfrontMIP: 5911,       // dollar amount: 1.75% × $337,750 base loan
      annualMIPRate: 0.0055,  // decimal rate: 0.55% annual MIP (LTV > 95%, term > 15yr)
      mipDurationYears: 30,   // permanent: LTV > 90% on < 10% down post-2013
    },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

const tabB4: ShareableTab = {
  id: 'art4-tab-b',
  title: 'Conventional 5% down',
  mortgageTerms: {
    country: 'USA',
    homePrice: 350000,
    downPaymentAmount: 17500,
    downPaymentPercentage: 5,
    mortgageType: 'fixed',
    loanProgram: 'conventional',
    rate: 6.75,
    amortizationPeriod: 30,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0.5 },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

export const FHA_VS_CONVENTIONAL_URL = buildScenarioUrl({
  version: 1,
  activeTabId: 'compare',
  tabs: [tabA4, tabB4],
});

// ---------------------------------------------------------------------------
// New guides (metrics locked in articleScenarioRegistry.ts + articleScenarioCoverage.test.ts)
// ---------------------------------------------------------------------------

const PITI_EXPENSES: AnnualExpensesData = {
  propertyTaxes: { type: 'amount', value: 450 },
  homeInsurance: { type: 'amount', value: 150 },
  hoaUtilities: { type: 'amount', value: 300 },
};

const cmpExtraA: ShareableTab = {
  id: 'cmp-extra-a',
  title: 'No extra payments',
  mortgageTerms: {
    country: 'USA',
    homePrice: 400000,
    downPaymentAmount: 80000,
    downPaymentPercentage: 20,
    mortgageType: 'fixed',
    loanProgram: 'conventional',
    rate: 6.5,
    amortizationPeriod: 30,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0 },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

const cmpExtraB: ShareableTab = {
  id: 'cmp-extra-b',
  title: '+$250/mo to principal',
  mortgageTerms: { ...cmpExtraA.mortgageTerms },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: { frequency: 'monthly', amount: 250, startDate: START_DATE as unknown as Date },
};

export const EXTRA_PAYMENTS_PAYOFF_URL = buildScenarioUrl({
  version: 1,
  activeTabId: 'compare',
  tabs: [cmpExtraA, cmpExtraB],
});

const caBiweeklyA: ShareableTab = {
  id: 'ca-biw-a',
  title: 'Monthly payments',
  mortgageTerms: {
    country: 'Canada',
    homePrice: 550000,
    downPaymentAmount: 110000,
    downPaymentPercentage: 20,
    mortgageType: 'fixed',
    loanProgram: 'conventional',
    rate: 5.4,
    amortizationPeriod: 25,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0 },
    canadianTermDetails: { termLength: 5, showRenewalScenario: false },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

const caBiweeklyB: ShareableTab = {
  id: 'ca-biw-b',
  title: 'Accelerated bi-weekly',
  mortgageTerms: {
    ...caBiweeklyA.mortgageTerms,
    paymentFrequency: 'accelerated-bi-weekly',
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

export const BIWEEKLY_VS_MONTHLY_CANADA_URL = buildScenarioUrl({
  version: 1,
  activeTabId: 'compare',
  tabs: [caBiweeklyA, caBiweeklyB],
});

const us15: ShareableTab = {
  id: 'cmp-15',
  title: '15-year fixed 6.25%',
  mortgageTerms: {
    country: 'USA',
    homePrice: 475000,
    downPaymentAmount: 95000,
    downPaymentPercentage: 20,
    mortgageType: 'fixed',
    loanProgram: 'conventional',
    rate: 6.25,
    amortizationPeriod: 15,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0 },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

const us30: ShareableTab = {
  id: 'cmp-30',
  title: '30-year fixed 6.75%',
  mortgageTerms: {
    ...us15.mortgageTerms,
    rate: 6.75,
    amortizationPeriod: 30,
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

export const COMPARE_OFFERS_15_30_URL = buildScenarioUrl({
  version: 1,
  activeTabId: 'compare',
  tabs: [us15, us30],
});

// Mortgage amortization schedules explained (US 15/20/30) guide
// Tab titles used in article prose and tables - must match ShareableTab.title exactly.
export const AMORT_SCHEDULE_US_15_TAB_TITLE = '15-year 6.5%';
export const AMORT_SCHEDULE_US_20_TAB_TITLE = '20-year 6.5%';
export const AMORT_SCHEDULE_US_30_TAB_TITLE = '30-year 6.5%';

const amtUsSharedTerms = {
  country: 'USA' as const,
  homePrice: 475000,
  downPaymentAmount: 95000,
  downPaymentPercentage: 20,
  mortgageType: 'fixed' as const,
  loanProgram: 'conventional' as const,
  rate: 6.5,
  startDate: START_DATE as unknown as Date,
  paymentFrequency: 'monthly' as const,
  pmi: { type: 'percent' as const, value: 0 },
};

const amtUs15: ShareableTab = {
  id: 'amt-us-15',
  title: AMORT_SCHEDULE_US_15_TAB_TITLE,
  mortgageTerms: {
    ...amtUsSharedTerms,
    amortizationPeriod: 15,
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

const amtUs20: ShareableTab = {
  id: 'amt-us-20',
  title: AMORT_SCHEDULE_US_20_TAB_TITLE,
  mortgageTerms: {
    ...amtUsSharedTerms,
    amortizationPeriod: 20,
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

const amtUs30: ShareableTab = {
  id: 'amt-us-30',
  title: AMORT_SCHEDULE_US_30_TAB_TITLE,
  mortgageTerms: {
    ...amtUsSharedTerms,
    amortizationPeriod: 30,
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

/** US 15/20/30 same rate: opens Compare with three tabs (max share tabs). */
export const AMORT_SCHEDULE_US_15_20_30_URL = buildScenarioUrl({
  version: 1,
  activeTabId: 'compare',
  tabs: [amtUs15, amtUs20, amtUs30],
});

const usFixed685: ShareableTab = {
  id: 'which-fixed',
  title: '30yr fixed 6.85%',
  mortgageTerms: {
    country: 'USA',
    homePrice: 525000,
    downPaymentAmount: 105000,
    downPaymentPercentage: 20,
    mortgageType: 'fixed',
    loanProgram: 'conventional',
    rate: 6.85,
    amortizationPeriod: 30,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0 },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

const usArm710: ShareableTab = {
  id: 'which-arm',
  title: '7/1 ARM 6.10%',
  mortgageTerms: {
    ...usFixed685.mortgageTerms,
    mortgageType: 'adjustable',
    rate: 6.1,
    armDetails: {
      initialFixedPeriod: 7,
      adjustmentFrequency: 12,
      indexRate: 4.2,
      margin: 1.75,
      initialAdjustmentCap: 2,
      periodicCap: 2,
      lifetimeCap: 5,
    },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

export const FIXED_VARIABLE_OR_ARM_US_URL = buildScenarioUrl({
  version: 1,
  activeTabId: 'compare',
  tabs: [usFixed685, usArm710],
});

/** Same two-tab ARM demo as existing fixed-vs-ARM USA article. */
export const MODEL_ARM_BEFORE_BORROW_URL = FIXED_VS_ARM_USA_URL;

const pitiFull: ShareableTab = {
  id: 'piti-full',
  title: 'PITI + HOA (modeled)',
  mortgageTerms: {
    country: 'USA',
    homePrice: 450000,
    downPaymentAmount: 45000,
    downPaymentPercentage: 10,
    mortgageType: 'fixed',
    loanProgram: 'conventional',
    rate: 6.9,
    amortizationPeriod: 30,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0.55 },
  },
  annualExpenses: PITI_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

const pitiPi: ShareableTab = {
  id: 'piti-pi',
  title: 'PI + PMI only (same loan)',
  mortgageTerms: { ...pitiFull.mortgageTerms },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

export const TRUE_MONTHLY_PITI_HOA_URL = buildScenarioUrl({
  version: 1,
  activeTabId: 'compare',
  tabs: [pitiPi, pitiFull],
});

const caAmort25: ShareableTab = {
  id: 'ca-am-25',
  title: '25-year amortization',
  mortgageTerms: {
    country: 'Canada',
    homePrice: 625000,
    downPaymentAmount: 125000,
    downPaymentPercentage: 20,
    mortgageType: 'fixed',
    loanProgram: 'conventional',
    rate: 5.15,
    amortizationPeriod: 25,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0 },
    canadianTermDetails: { termLength: 5, showRenewalScenario: false },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

const caAmort30: ShareableTab = {
  id: 'ca-am-30',
  title: '30-year amortization (same rate)',
  mortgageTerms: {
    ...caAmort25.mortgageTerms,
    amortizationPeriod: 30,
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

export const AMORTIZATION_SCHEDULE_EXPLAINED_CANADA_URL = buildScenarioUrl({
  version: 1,
  activeTabId: 'compare',
  tabs: [caAmort25, caAmort30],
});

const fthbLow: ShareableTab = {
  id: 'fthb-low',
  title: '5% down + PMI',
  mortgageTerms: {
    country: 'USA',
    homePrice: 380000,
    downPaymentAmount: 19000,
    downPaymentPercentage: 5,
    mortgageType: 'fixed',
    loanProgram: 'conventional',
    rate: 7.0,
    amortizationPeriod: 30,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0.62 },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

const fthbHigh: ShareableTab = {
  id: 'fthb-high',
  title: '20% down, no PMI',
  mortgageTerms: {
    country: 'USA',
    homePrice: 380000,
    downPaymentAmount: 76000,
    downPaymentPercentage: 20,
    mortgageType: 'fixed',
    loanProgram: 'conventional',
    rate: 6.75,
    amortizationPeriod: 30,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0 },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

export const FTHB_CALCULATOR_US_URL = buildScenarioUrl({
  version: 1,
  activeTabId: 'compare',
  tabs: [fthbLow, fthbHigh],
});

const pmi5: ShareableTab = {
  id: 'pmi-5',
  title: '5% down + PMI 0.58%',
  mortgageTerms: {
    country: 'USA',
    homePrice: 400000,
    downPaymentAmount: 20000,
    downPaymentPercentage: 5,
    mortgageType: 'fixed',
    loanProgram: 'conventional',
    rate: 6.8,
    amortizationPeriod: 30,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0.58 },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

const pmi20: ShareableTab = {
  id: 'pmi-20',
  title: '20% down, no PMI',
  mortgageTerms: {
    ...pmi5.mortgageTerms,
    downPaymentAmount: 80000,
    downPaymentPercentage: 20,
    pmi: { type: 'percent', value: 0 },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

export const PMI_WHEN_IT_GOES_AWAY_URL = buildScenarioUrl({
  version: 1,
  activeTabId: 'compare',
  tabs: [pmi5, pmi20],
});

const refiKeep: ShareableTab = {
  id: 'refi-keep',
  title: 'Keep: 22yr left at 6.875%',
  mortgageTerms: {
    country: 'USA',
    homePrice: 378577,
    downPaymentAmount: 100000,
    downPaymentPercentage: 20.9,
    mortgageType: 'fixed',
    loanProgram: 'conventional',
    rate: 6.875,
    amortizationPeriod: 22,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0 },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

const refiNew: ShareableTab = {
  id: 'refi-new',
  title: 'Refi: 30yr at 5.875% + $6k costs',
  mortgageTerms: {
    country: 'USA',
    homePrice: 384577,
    downPaymentAmount: 100000,
    downPaymentPercentage: 20.6,
    mortgageType: 'fixed',
    loanProgram: 'conventional',
    rate: 5.875,
    amortizationPeriod: 30,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0 },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

export const REFINANCE_BREAK_EVEN_URL = buildScenarioUrl({
  version: 1,
  activeTabId: 'compare',
  tabs: [refiKeep, refiNew],
});

// ---------------------------------------------------------------------------
// Article 16: Bi-weekly vs monthly mortgage payments - US
// $500k home, 20% down ($400k loan), 30-year fixed at 6.75%
// Tab A: Standard monthly | Tab B: Manual $100/mo extra to principal
// (US servicers do not natively offer accelerated bi-weekly; modeled as prepay)
// ---------------------------------------------------------------------------

const usBiweeklyMonthly: ShareableTab = {
  id: 'us-biw-monthly',
  title: 'Standard monthly',
  mortgageTerms: {
    country: 'USA',
    homePrice: 500000,
    downPaymentAmount: 100000,
    downPaymentPercentage: 20,
    mortgageType: 'fixed',
    loanProgram: 'conventional',
    rate: 6.75,
    amortizationPeriod: 30,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0 },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

const usBiweeklyPrepay: ShareableTab = {
  id: 'us-biw-prepay',
  title: 'Monthly + $216/mo (bi-weekly equivalent)',
  mortgageTerms: { ...usBiweeklyMonthly.mortgageTerms },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: { frequency: 'monthly', amount: 216, startDate: START_DATE as unknown as Date },
};

export const BIWEEKLY_VS_MONTHLY_US_URL = buildScenarioUrl({
  version: 1,
  activeTabId: 'compare',
  tabs: [usBiweeklyMonthly, usBiweeklyPrepay],
});

// ---------------------------------------------------------------------------
// Reddit: accelerated bi-weekly vs monthly + $216 principal (r/Mortgages)
// Same $500k / 20% / $400k / 6.75% / 30yr skeleton as Article 16
// ---------------------------------------------------------------------------

export const REDDIT_BIWEEKLY_VS_PREPAY_ACCEL_TAB_TITLE = 'Accel. Bi-Weekly';

export const REDDIT_BIWEEKLY_VS_PREPAY_MONTHLY_PREPAY_TAB_TITLE =
  'Monthly + $216/mo to principal';

const redditBiweeklyAccel: ShareableTab = {
  id: 'reddit-biw-accel',
  title: REDDIT_BIWEEKLY_VS_PREPAY_ACCEL_TAB_TITLE,
  mortgageTerms: {
    ...usBiweeklyMonthly.mortgageTerms,
    paymentFrequency: 'accelerated-bi-weekly',
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

const redditBiweeklyMonthlyPrepay: ShareableTab = {
  id: 'reddit-biw-prep',
  title: REDDIT_BIWEEKLY_VS_PREPAY_MONTHLY_PREPAY_TAB_TITLE,
  mortgageTerms: { ...usBiweeklyMonthly.mortgageTerms },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: { frequency: 'monthly', amount: 216, startDate: START_DATE as unknown as Date },
};

export const REDDIT_BIWEEKLY_VS_PREPAY_PRINCIPAL_US_SHARE: ShareState = {
  version: 1,
  activeTabId: 'compare',
  tabs: [redditBiweeklyAccel, redditBiweeklyMonthlyPrepay],
};

export const REDDIT_BIWEEKLY_VS_PREPAY_PRINCIPAL_US_URL = buildScenarioUrl(
  REDDIT_BIWEEKLY_VS_PREPAY_PRINCIPAL_US_SHARE
);

// ---------------------------------------------------------------------------
// Article 17: Canadian renewal shock scenario
// $520k purchase, 20% down ($416k mortgage), 5-year term
// Tab A: Original 3.49% fixed, renews at 3.49% (baseline, no shock)
// Tab B: Original 3.49% fixed, renews at 6.25% (shock scenario)
// ---------------------------------------------------------------------------

const caRenewalBase: ShareableTab = {
  id: 'ca-renew-base',
  title: 'Renew at 3.49% (baseline)',
  mortgageTerms: {
    country: 'Canada',
    homePrice: 520000,
    downPaymentAmount: 104000,
    downPaymentPercentage: 20,
    mortgageType: 'fixed',
    loanProgram: 'conventional',
    rate: 3.49,
    amortizationPeriod: 25,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0 },
    canadianTermDetails: {
      termLength: 5,
      showRenewalScenario: true,
      renewalRate: 3.49,
    },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

const caRenewalShock: ShareableTab = {
  id: 'ca-renew-shock',
  title: 'Renew at 6.25% (shock)',
  mortgageTerms: {
    ...caRenewalBase.mortgageTerms,
    canadianTermDetails: {
      termLength: 5,
      showRenewalScenario: true,
      renewalRate: 6.25,
    },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

export const CANADIAN_RENEWAL_SHOCK_URL = buildScenarioUrl({
  version: 1,
  activeTabId: 'compare',
  tabs: [caRenewalBase, caRenewalShock],
});

// ---------------------------------------------------------------------------
// Article 18: How much house can I afford - US
// Two affordability points on the same budget: $2,800/mo target PITI
// Tab A: $400k home at 6.50%, 10% down, PMI + taxes + insurance modeled
// Tab B: $340k home at 6.50%, 10% down, same expenses proportional
// ---------------------------------------------------------------------------

const AFFORDABILITY_EXPENSES_400K: AnnualExpensesData = {
  propertyTaxes: { type: 'amount', value: 400 },
  homeInsurance: { type: 'amount', value: 125 },
  hoaUtilities: { type: 'amount', value: 0 },
};

const AFFORDABILITY_EXPENSES_340K: AnnualExpensesData = {
  propertyTaxes: { type: 'amount', value: 340 },
  homeInsurance: { type: 'amount', value: 110 },
  hoaUtilities: { type: 'amount', value: 0 },
};

const affordability400: ShareableTab = {
  id: 'afford-400',
  title: '$400k stretch',
  mortgageTerms: {
    country: 'USA',
    homePrice: 400000,
    downPaymentAmount: 40000,
    downPaymentPercentage: 10,
    mortgageType: 'fixed',
    loanProgram: 'conventional',
    rate: 6.50,
    amortizationPeriod: 30,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0.55 },
  },
  annualExpenses: AFFORDABILITY_EXPENSES_400K,
  extraPayments: NO_EXTRA_PAYMENTS,
};

const affordability340: ShareableTab = {
  id: 'afford-340',
  title: '$340k comfortable',
  mortgageTerms: {
    country: 'USA',
    homePrice: 340000,
    downPaymentAmount: 34000,
    downPaymentPercentage: 10,
    mortgageType: 'fixed',
    loanProgram: 'conventional',
    rate: 6.50,
    amortizationPeriod: 30,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0.55 },
  },
  annualExpenses: AFFORDABILITY_EXPENSES_340K,
  extraPayments: NO_EXTRA_PAYMENTS,
};

export const AFFORDABILITY_US_URL = buildScenarioUrl({
  version: 1,
  activeTabId: 'compare',
  tabs: [affordability400, affordability340],
});

// ---------------------------------------------------------------------------
// Article 19: VA loan vs conventional (zero-down)
// $380k home purchase
// Tab A: VA loan, 0% down, 2.15% funding fee rolled in, 6.50%
// Tab B: Conventional, 5% down, 6.75% with 0.50% PMI
// ---------------------------------------------------------------------------

const vaTab: ShareableTab = {
  id: 'va-zero',
  title: 'VA 0% down (funding fee rolled in)',
  mortgageTerms: {
    country: 'USA',
    homePrice: 380000,
    downPaymentAmount: 0,
    downPaymentPercentage: 0,
    mortgageType: 'fixed',
    loanProgram: 'va',
    rate: 6.50,
    amortizationPeriod: 30,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0 },
    vaDetails: {
      fundingFeeRate: 0.0215,
      isFirstTimeUse: true,
      isDisabledVeteran: false,
    },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

const convTab: ShareableTab = {
  id: 'va-conv',
  title: 'Conventional 5% down + PMI',
  mortgageTerms: {
    country: 'USA',
    homePrice: 380000,
    downPaymentAmount: 19000,
    downPaymentPercentage: 5,
    mortgageType: 'fixed',
    loanProgram: 'conventional',
    rate: 6.75,
    amortizationPeriod: 30,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0.50 },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

export const VA_VS_CONVENTIONAL_URL = buildScenarioUrl({
  version: 1,
  activeTabId: 'compare',
  tabs: [vaTab, convTab],
});

// ---------------------------------------------------------------------------
// r/Mortgages: 25% down vs 20% down + 5% of price as one-time extra (month 1)
// $500k US, 6% fixed, 30yr conventional, P&I only. Locked in articleScenarioMetrics.
// ---------------------------------------------------------------------------

/** Compare-tab label for the 25% scenario (must match ShareableTab.title; used in Learn article copy). */
export const REDDIT_DOWN_PAYMENT_VS_PREPAY_25_TAB_TITLE = '25% down';

const REDDIT_DP_TAB_25: ShareableTab = {
  id: 'reddit-dp-25',
  title: REDDIT_DOWN_PAYMENT_VS_PREPAY_25_TAB_TITLE,
  mortgageTerms: {
    country: 'USA',
    homePrice: 500000,
    downPaymentAmount: 125000,
    downPaymentPercentage: 25,
    mortgageType: 'fixed',
    loanProgram: 'conventional',
    rate: 6,
    amortizationPeriod: 30,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0 },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

/** Compare-tab label (must match ShareableTab.title below; used in Learn article copy). */
export const REDDIT_DOWN_PAYMENT_VS_PREPAY_OTP_TAB_TITLE = '20% down with 5% one time payment';

const REDDIT_DP_TAB_20_OTP: ShareableTab = {
  id: 'reddit-dp-20-otp',
  title: REDDIT_DOWN_PAYMENT_VS_PREPAY_OTP_TAB_TITLE,
  mortgageTerms: {
    country: 'USA',
    homePrice: 500000,
    downPaymentAmount: 100000,
    downPaymentPercentage: 20,
    mortgageType: 'fixed',
    loanProgram: 'conventional',
    rate: 6,
    amortizationPeriod: 30,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0 },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
  otpEdits: [{ idx: 0, amount: 500000 * 0.05 }],
};

/** Pre-built `?s=` state for tests and article metrics (do not duplicate tab defs). */
export const REDDIT_DOWN_PAYMENT_VS_PREPAY_US_SHARE: ShareState = {
  version: 1,
  activeTabId: 'reddit-dp-25',
  tabs: [REDDIT_DP_TAB_25, REDDIT_DP_TAB_20_OTP],
};

export const REDDIT_DOWN_PAYMENT_VS_PREPAY_US_URL = buildScenarioUrl(REDDIT_DOWN_PAYMENT_VS_PREPAY_US_SHARE);

// ---------------------------------------------------------------------------
// Article: 15-Year vs 30-Year Mortgage (US, same rate)
// $500k purchase, 20% down ($400k loan), 6.50% fixed, monthly
// Tab A: 15-year | Tab B: 30-year
// ---------------------------------------------------------------------------

export const FIFTEEN_VS_THIRTY_15_TAB_TITLE = '15-Year Fixed 6.50%';
export const FIFTEEN_VS_THIRTY_30_TAB_TITLE = '30-Year Fixed 6.50%';

const fifteenVsThirty15: ShareableTab = {
  id: 'term-15',
  title: FIFTEEN_VS_THIRTY_15_TAB_TITLE,
  mortgageTerms: {
    country: 'USA',
    homePrice: 500000,
    downPaymentAmount: 100000,
    downPaymentPercentage: 20,
    mortgageType: 'fixed',
    loanProgram: 'conventional',
    rate: 6.5,
    amortizationPeriod: 15,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0 },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

const fifteenVsThirty30: ShareableTab = {
  id: 'term-30',
  title: FIFTEEN_VS_THIRTY_30_TAB_TITLE,
  mortgageTerms: {
    ...fifteenVsThirty15.mortgageTerms,
    amortizationPeriod: 30,
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

export const FIFTEEN_VS_THIRTY_YEAR_US_URL = buildScenarioUrl({
  version: 1,
  activeTabId: 'compare',
  tabs: [fifteenVsThirty15, fifteenVsThirty30],
});

// ---------------------------------------------------------------------------
// Article: Down payment 5% / 10% / 20% compared (US)
// $400k home, 6.75% fixed, 30yr conventional, monthly, P&I + PMI only
// Three tabs (max share tabs)
// ---------------------------------------------------------------------------

export const DOWN_PAYMENT_5_TAB_TITLE = '5% down + PMI';
export const DOWN_PAYMENT_10_TAB_TITLE = '10% down + PMI';
export const DOWN_PAYMENT_20_TAB_TITLE = '20% down, no PMI';

const dpSharedTerms = {
  country: 'USA' as const,
  homePrice: 400000,
  mortgageType: 'fixed' as const,
  loanProgram: 'conventional' as const,
  rate: 6.75,
  amortizationPeriod: 30,
  startDate: START_DATE as unknown as Date,
  paymentFrequency: 'monthly' as const,
};

const dp5: ShareableTab = {
  id: 'dp-5',
  title: DOWN_PAYMENT_5_TAB_TITLE,
  mortgageTerms: {
    ...dpSharedTerms,
    downPaymentAmount: 20000,
    downPaymentPercentage: 5,
    pmi: { type: 'percent', value: 0.55 },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

const dp10: ShareableTab = {
  id: 'dp-10',
  title: DOWN_PAYMENT_10_TAB_TITLE,
  mortgageTerms: {
    ...dpSharedTerms,
    downPaymentAmount: 40000,
    downPaymentPercentage: 10,
    pmi: { type: 'percent', value: 0.35 },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

const dp20: ShareableTab = {
  id: 'dp-20',
  title: DOWN_PAYMENT_20_TAB_TITLE,
  mortgageTerms: {
    ...dpSharedTerms,
    downPaymentAmount: 80000,
    downPaymentPercentage: 20,
    pmi: { type: 'percent', value: 0 },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

export const DOWN_PAYMENT_5_10_20_US_URL = buildScenarioUrl({
  version: 1,
  activeTabId: 'compare',
  tabs: [dp5, dp10, dp20],
});

// ---------------------------------------------------------------------------
// Reddit: how mortgage prepayment works ($100k loan, 6%, 30yr, $10K OTP at pmt 4)
// r/Mortgages: "How does mortgage prepayment work?"
// $125k home, 20% down ($100k loan), 6% fixed, 30yr
// Tab A: $10K one-time payment on Payment #4 (otpEdits idx 3)
// Tab B: Standard schedule (no extras)
// ---------------------------------------------------------------------------

export const REDDIT_PREPAYMENT_OTP_TAB_TITLE = '$10K extra at payment 4';
export const REDDIT_PREPAYMENT_STANDARD_TAB_TITLE = 'Standard payments';

const redditPrepayOtp: ShareableTab = {
  id: 'reddit-prepay-otp',
  title: REDDIT_PREPAYMENT_OTP_TAB_TITLE,
  mortgageTerms: {
    country: 'USA',
    homePrice: 125000,
    downPaymentAmount: 25000,
    downPaymentPercentage: 20,
    mortgageType: 'fixed',
    loanProgram: 'conventional',
    rate: 6,
    amortizationPeriod: 30,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0 },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
  otpEdits: [{ idx: 3, amount: 10000 }],
};

const redditPrepayStandard: ShareableTab = {
  id: 'reddit-prepay-std',
  title: REDDIT_PREPAYMENT_STANDARD_TAB_TITLE,
  mortgageTerms: { ...redditPrepayOtp.mortgageTerms },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

export const REDDIT_PREPAYMENT_HOW_IT_WORKS_US_SHARE: ShareState = {
  version: 1,
  activeTabId: 'reddit-prepay-otp',
  tabs: [redditPrepayOtp, redditPrepayStandard],
};

export const REDDIT_PREPAYMENT_HOW_IT_WORKS_US_URL = buildScenarioUrl(
  REDDIT_PREPAYMENT_HOW_IT_WORKS_US_SHARE
);

// ---------------------------------------------------------------------------
// Article: CMHC mortgage insurance explained (Canada)
// $650k Ontario purchase, 5.10% fixed, 25yr amort, 5yr term
// Tab A: CMHC insured at stepped minimum down ($40k) | Tab B: Conventional 20% down
// ---------------------------------------------------------------------------

export const CMHC_INSURED_TAB_TITLE = 'CMHC insured (minimum down)';
export const CONVENTIONAL_20_TAB_TITLE = 'Conventional (20% down)';

const CMHC_ARTICLE_RATE = 5.1;

const cmhcInsuredOntario: ShareableTab = {
  id: 'cmhc-insured-min-down',
  title: CMHC_INSURED_TAB_TITLE,
  mortgageTerms: {
    country: 'Canada',
    homePrice: 650000,
    downPaymentAmount: 40000,
    downPaymentPercentage: 6.15,
    mortgageType: 'fixed',
    loanProgram: 'cmhc',
    rate: CMHC_ARTICLE_RATE,
    amortizationPeriod: 25,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0 },
    canadianTermDetails: { termLength: 5, showRenewalScenario: false },
    cmhcDetails: { premiumRate: 0.04, province: 'ON', includeProvincialTax: false },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

const cmhcConventionalOntario: ShareableTab = {
  id: 'cmhc-conventional-20',
  title: CONVENTIONAL_20_TAB_TITLE,
  mortgageTerms: {
    country: 'Canada',
    homePrice: 650000,
    downPaymentAmount: 130000,
    downPaymentPercentage: 20,
    mortgageType: 'fixed',
    loanProgram: 'conventional',
    rate: CMHC_ARTICLE_RATE,
    amortizationPeriod: 25,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0 },
    canadianTermDetails: { termLength: 5, showRenewalScenario: false },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

export const CMHC_MORTGAGE_INSURANCE_EXPLAINED_URL = buildScenarioUrl({
  version: 1,
  activeTabId: 'compare',
  tabs: [cmhcInsuredOntario, cmhcConventionalOntario],
});

// ---------------------------------------------------------------------------
// Article: Closing costs in Canada (Toronto worked example)
// $800k Toronto purchase, 10% down, CMHC insured, 25yr amort, 5yr term
// Rate is provisional (5.10%) until article placeholders are finalized.
// ---------------------------------------------------------------------------

export const CLOSING_COSTS_TORONTO_TAB_TITLE = 'Toronto $800k, 10% down';

/** Provisional rate wired in articleScenarios.ts; replace when placeholders are finalized. */
const CLOSING_COSTS_ARTICLE_RATE = 5.1;

const closingCostsToronto: ShareableTab = {
  id: 'closing-costs-toronto-800k',
  title: CLOSING_COSTS_TORONTO_TAB_TITLE,
  mortgageTerms: {
    country: 'Canada',
    homePrice: 800000,
    downPaymentAmount: 80000,
    downPaymentPercentage: 10,
    mortgageType: 'fixed',
    loanProgram: 'cmhc',
    rate: CLOSING_COSTS_ARTICLE_RATE,
    amortizationPeriod: 25,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0 },
    canadianTermDetails: { termLength: 5, showRenewalScenario: false },
    cmhcDetails: { premiumRate: 0.031, province: 'ON', includeProvincialTax: false },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

export const CLOSING_COSTS_CANADA_URL = buildScenarioUrl({
  version: 1,
  activeTabId: 'closing-costs-toronto-800k',
  tabs: [closingCostsToronto],
});

// ---------------------------------------------------------------------------
// Article: Canadian mortgage stress test (GDS/TDS qualifying rate)
// $120k income; max-GDS mortgage at contract vs qualifying rate.
// Mortgage tabs, monthly property tax, and GDS/TDS inputs pre-fill via share link.
// ---------------------------------------------------------------------------

export const STRESS_TEST_CONTRACT_TAB_TITLE = 'Contract rate 5.10%';
export const STRESS_TEST_QUALIFYING_TAB_TITLE = 'Qualifying rate 7.10%';

export const STRESS_TEST_MONTHLY_PROPERTY_TAX = 400;
/** Annual equivalent for article copy ($400/mo). */
export const STRESS_TEST_ANNUAL_PROPERTY_TAX = STRESS_TEST_MONTHLY_PROPERTY_TAX * 12;
export const STRESS_TEST_MAX_MORTGAGE_CONTRACT = 570_397;
export const STRESS_TEST_MAX_MORTGAGE_QUALIFYING = 474_101;

const STRESS_TEST_GDS_TDS = {
  monthlyIncome: 10_000,
  heatingCosts: 150,
  condoFees: 0,
  otherDebts: 0,
};

const stressTestExpenses: AnnualExpensesData = {
  propertyTaxes: { type: 'amount', value: STRESS_TEST_MONTHLY_PROPERTY_TAX },
  homeInsurance: { type: 'amount', value: 0 },
  hoaUtilities: { type: 'amount', value: 0 },
};

const stressTestContract510: ShareableTab = {
  id: 'stress-test-contract-510',
  title: STRESS_TEST_CONTRACT_TAB_TITLE,
  mortgageTerms: {
    country: 'Canada',
    homePrice: 712996,
    downPaymentAmount: 142599,
    downPaymentPercentage: 20,
    mortgageType: 'fixed',
    loanProgram: 'conventional',
    rate: 5.1,
    amortizationPeriod: 25,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0 },
    canadianTermDetails: { termLength: 5, showRenewalScenario: false },
  },
  annualExpenses: stressTestExpenses,
  extraPayments: NO_EXTRA_PAYMENTS,
  gdsTds: STRESS_TEST_GDS_TDS,
};

const stressTestQualifying710: ShareableTab = {
  id: 'stress-test-qualifying-710',
  title: STRESS_TEST_QUALIFYING_TAB_TITLE,
  mortgageTerms: {
    country: 'Canada',
    homePrice: 592626,
    downPaymentAmount: 118525,
    downPaymentPercentage: 20,
    mortgageType: 'fixed',
    loanProgram: 'conventional',
    rate: 7.1,
    amortizationPeriod: 25,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0 },
    canadianTermDetails: { termLength: 5, showRenewalScenario: false },
  },
  annualExpenses: stressTestExpenses,
  extraPayments: NO_EXTRA_PAYMENTS,
  gdsTds: STRESS_TEST_GDS_TDS,
};

export const CANADIAN_MORTGAGE_STRESS_TEST_SHARE: ShareState = {
  version: 1,
  activeTabId: 'compare',
  tabs: [stressTestContract510, stressTestQualifying710],
};

export const CANADIAN_MORTGAGE_STRESS_TEST_URL = buildScenarioUrl(
  CANADIAN_MORTGAGE_STRESS_TEST_SHARE
);

// ---------------------------------------------------------------------------
// Article: Canadian semi-annual compounding vs US monthly (same nominal rate)
// $625k purchase, 20% down ($500k mortgage), 5.00% fixed, 25-year amortization
// Tab A: Canada fixed (semi-annual) | Tab B: USA fixed (monthly compounding)
// ---------------------------------------------------------------------------

export const SEMI_ANNUAL_CA_TAB_TITLE = 'Canada fixed (semi-annual)';
export const SEMI_ANNUAL_US_TAB_TITLE = 'USA fixed (monthly)';

const semiAnnualCaTab: ShareableTab = {
  id: 'semi-annual-ca-fixed',
  title: SEMI_ANNUAL_CA_TAB_TITLE,
  mortgageTerms: {
    country: 'Canada',
    homePrice: 625000,
    downPaymentAmount: 125000,
    downPaymentPercentage: 20,
    mortgageType: 'fixed',
    loanProgram: 'conventional',
    rate: 5,
    amortizationPeriod: 25,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0 },
    canadianTermDetails: { termLength: 5, showRenewalScenario: false },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

const semiAnnualUsTab: ShareableTab = {
  id: 'semi-annual-us-fixed',
  title: SEMI_ANNUAL_US_TAB_TITLE,
  mortgageTerms: {
    country: 'USA',
    homePrice: 625000,
    downPaymentAmount: 125000,
    downPaymentPercentage: 20,
    mortgageType: 'fixed',
    loanProgram: 'conventional',
    rate: 5,
    amortizationPeriod: 25,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0 },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

export const CANADIAN_SEMI_ANNUAL_COMPOUNDING_SHARE: ShareState = {
  version: 1,
  activeTabId: 'compare',
  tabs: [semiAnnualCaTab, semiAnnualUsTab],
};

export const CANADIAN_SEMI_ANNUAL_COMPOUNDING_URL = buildScenarioUrl(
  CANADIAN_SEMI_ANNUAL_COMPOUNDING_SHARE
);

// ---------------------------------------------------------------------------
// Article: Debt-to-income ratio for US mortgages
// $90k income, $670/mo debt, 20% down, 6.75%, 30yr conventional at max price
// Compare tab: $296k purchase (28/36 ceiling), P&I only
// ---------------------------------------------------------------------------

export const DTI_MORTGAGE_US_COMPARE_TAB_TITLE = '$296k at 6.75% (30-year)';

export const DTI_MORTGAGE_US_AFFORDABILITY_INPUTS: Partial<AffordabilityInputs> = {
  annualIncome: 90_000,
  monthlyDebtPayback: 670,
  loanTermYears: 30,
  contractRate: 6.75,
  country: 'USA',
  dtiRule: 'conventional',
  downPaymentPct: 20,
  propertyTax: { type: 'percent', value: 1.5 },
  hoa: { type: 'percent', value: 0 },
  insurance: { type: 'percent', value: 0.5 },
  closingCost: { type: 'percent', value: 3 },
  maintenance: { type: 'percent', value: 1.5 },
  monthlyHeating: 150,
};

const dtiCompare296: ShareableTab = {
  id: 'dti-296k-30',
  title: DTI_MORTGAGE_US_COMPARE_TAB_TITLE,
  mortgageTerms: {
    country: 'USA',
    homePrice: 296_000,
    downPaymentAmount: 59_200,
    downPaymentPercentage: 20,
    mortgageType: 'fixed',
    loanProgram: 'conventional',
    rate: 6.75,
    amortizationPeriod: 30,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0 },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

export const DTI_MORTGAGE_US_COMPARE_URL = buildScenarioUrl({
  version: 1,
  activeTabId: 'dti-296k-30',
  tabs: [dtiCompare296],
});

// ---------------------------------------------------------------------------
// Article: Pay off mortgage early - three US strategies ($380k / 6.5% / 30yr)
// Tab A: Baseline (no extras) | Tab B: +$300/mo recurring from month 1
// OTP $20K and +$200/mo bi-weekly equivalent modeled in article copy/tests
// ---------------------------------------------------------------------------

export const PAY_OFF_EARLY_BASELINE_TAB_TITLE = 'No extra payments';
export const PAY_OFF_EARLY_300_TAB_TITLE = '+$300/mo to principal';

const payOffEarlyBaseline: ShareableTab = {
  id: 'pay-off-early-baseline',
  title: PAY_OFF_EARLY_BASELINE_TAB_TITLE,
  mortgageTerms: {
    country: 'USA',
    homePrice: 380000,
    downPaymentAmount: 0,
    downPaymentPercentage: 0,
    mortgageType: 'fixed',
    loanProgram: 'conventional',
    rate: 6.5,
    amortizationPeriod: 30,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0 },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

const payOffEarly300: ShareableTab = {
  id: 'pay-off-early-300',
  title: PAY_OFF_EARLY_300_TAB_TITLE,
  mortgageTerms: { ...payOffEarlyBaseline.mortgageTerms },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: { frequency: 'monthly', amount: 300, startDate: START_DATE as unknown as Date },
};

export const PAY_OFF_EARLY_THREE_STRATEGIES_US_SHARE: ShareState = {
  version: 1,
  activeTabId: 'compare',
  tabs: [payOffEarlyBaseline, payOffEarly300],
};

export const PAY_OFF_EARLY_THREE_STRATEGIES_US_URL = buildScenarioUrl(
  PAY_OFF_EARLY_THREE_STRATEGIES_US_SHARE
);

// ---------------------------------------------------------------------------
// USDA vs FHA loan comparison - USA
// $300k purchase, 6.50% fixed, 30 years
// Tab A: USDA 0% down (1% upfront guarantee fee rolled in, 0.35% annual fee)
// Tab B: FHA 3.5% down ($10,500), 1.75% UFMIP rolled in, 0.55% annual MIP
// Locked in articleScenarioRegistry.ts
// ---------------------------------------------------------------------------

export const USDA_VS_FHA_USDA_TAB_TITLE = 'USDA 0% down';
export const USDA_VS_FHA_FHA_TAB_TITLE = 'FHA 3.5% down';

const usdaVsFhaUsdaTab: ShareableTab = {
  id: 'usda-vs-fha-usda',
  title: USDA_VS_FHA_USDA_TAB_TITLE,
  mortgageTerms: {
    country: 'USA',
    homePrice: 300000,
    downPaymentAmount: 0,
    downPaymentPercentage: 0,
    mortgageType: 'fixed',
    loanProgram: 'usda',
    rate: 6.50,
    amortizationPeriod: 30,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0 },
    usdaDetails: {
      upfrontFee: 0.01,
      annualFee: 0.0035,
    },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

const usdaVsFhaFhaTab: ShareableTab = {
  id: 'usda-vs-fha-fha',
  title: USDA_VS_FHA_FHA_TAB_TITLE,
  mortgageTerms: {
    country: 'USA',
    homePrice: 300000,
    downPaymentAmount: 10500,
    downPaymentPercentage: 3.5,
    mortgageType: 'fixed',
    loanProgram: 'fha',
    rate: 6.50,
    amortizationPeriod: 30,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0 },
    fhaDetails: {
      upfrontMIP: 5066,
      annualMIPRate: 0.0055,
      mipDurationYears: 30,
    },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

export const USDA_VS_FHA_URL = buildScenarioUrl({
  version: 1,
  activeTabId: 'compare',
  tabs: [usdaVsFhaUsdaTab, usdaVsFhaFhaTab],
});

// ---------------------------------------------------------------------------
// Canada VRM vs ARM - trigger rate comparison
// $560k purchase, 20% down ($450k mortgage), prime 5.20% minus 0.75% = 4.45%
// Tab A: VRM (fixed payment) | Tab B: ARM (adjustable payment)
// Rate forecast: +1.25% prime at year 2, +0.75% at year 3
// Locked in articleScenarioRegistry.ts
// ---------------------------------------------------------------------------

export const VRM_ARM_CA_VRM_TAB_TITLE = 'VRM - fixed payment';
export const VRM_ARM_CA_ARM_TAB_TITLE = 'ARM - adjustable payment';

const vrmArmRateChanges = [{ year: 2, delta: 1.25 }, { year: 3, delta: 0.75 }];

const vrmArmSharedMortgageTerms = {
  country: 'Canada' as const,
  homePrice: 560000,
  downPaymentAmount: 112000,
  downPaymentPercentage: 20,
  mortgageType: 'variable' as const,
  loanProgram: 'conventional' as const,
  rate: 4.45,
  amortizationPeriod: 25,
  startDate: START_DATE as unknown as Date,
  paymentFrequency: 'monthly' as const,
  pmi: { type: 'percent' as const, value: 0 },
  canadianTermDetails: { termLength: 5, showRenewalScenario: false },
};

const vrmArmCaVrmTab: ShareableTab = {
  id: 'vrm-arm-ca-vrm',
  title: VRM_ARM_CA_VRM_TAB_TITLE,
  mortgageTerms: {
    ...vrmArmSharedMortgageTerms,
    variableDetails: {
      primeRate: 5.2,
      discount: 0.75,
      variableType: 'vrm',
      rateChanges: vrmArmRateChanges,
    },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

const vrmArmCaArmTab: ShareableTab = {
  id: 'vrm-arm-ca-arm',
  title: VRM_ARM_CA_ARM_TAB_TITLE,
  mortgageTerms: {
    ...vrmArmSharedMortgageTerms,
    variableDetails: {
      primeRate: 5.2,
      discount: 0.75,
      variableType: 'arm',
      rateChanges: vrmArmRateChanges,
    },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

export const VRM_ARM_CA_URL = buildScenarioUrl({
  version: 1,
  activeTabId: 'compare',
  tabs: [vrmArmCaVrmTab, vrmArmCaArmTab],
});

// ---------------------------------------------------------------------------
// Article: GDS and TDS qualification ratios (Canada)
// $110k household income; contract-rate P&I sized to ~$2,800/mo with $750 other debt.
// ---------------------------------------------------------------------------

export const GDS_TDS_WORKED_EXAMPLE_TAB_TITLE = 'Worked example ($110k income)';

export const GDS_TDS_MONTHLY_PROPERTY_TAX = 350;
/** Annual equivalent for article copy ($350/mo). */
export const GDS_TDS_ANNUAL_PROPERTY_TAX = GDS_TDS_MONTHLY_PROPERTY_TAX * 12;
export const GDS_TDS_MONTHLY_INCOME = 9167;
export const GDS_TDS_OTHER_DEBTS = 750;

const GDS_TDS_PANEL = {
  monthlyIncome: GDS_TDS_MONTHLY_INCOME,
  heatingCosts: 150,
  condoFees: 0,
  otherDebts: GDS_TDS_OTHER_DEBTS,
};

const gdsTdsExpenses: AnnualExpensesData = {
  propertyTaxes: { type: 'amount', value: GDS_TDS_MONTHLY_PROPERTY_TAX },
  homeInsurance: { type: 'amount', value: 0 },
  hoaUtilities: { type: 'amount', value: 0 },
};

const gdsTdsWorkedExampleTab: ShareableTab = {
  id: 'gds-tds-worked-example',
  title: GDS_TDS_WORKED_EXAMPLE_TAB_TITLE,
  mortgageTerms: {
    country: 'Canada',
    homePrice: 596000,
    downPaymentAmount: 119200,
    downPaymentPercentage: 20,
    mortgageType: 'fixed',
    loanProgram: 'conventional',
    rate: 5.1,
    amortizationPeriod: 25,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0 },
    canadianTermDetails: { termLength: 5, showRenewalScenario: false },
  },
  annualExpenses: gdsTdsExpenses,
  extraPayments: NO_EXTRA_PAYMENTS,
  gdsTds: GDS_TDS_PANEL,
};

export const GDS_TDS_QUALIFICATION_CANADA_URL = buildScenarioUrl({
  version: 1,
  activeTabId: 'gds-tds-worked-example',
  tabs: [gdsTdsWorkedExampleTab],
});

// ---------------------------------------------------------------------------
// Article: $500,000 mortgage monthly payment by rate (US)
// $500k loan modeled as 20% down on $625k purchase, conventional fixed, monthly P&I
// Three compare tabs: 30-year at 5.50%, 6.50%, 7.50%
// ---------------------------------------------------------------------------

const m500kLoanBaseTerms = {
  country: 'USA' as const,
  homePrice: 625_000,
  downPaymentAmount: 125_000,
  downPaymentPercentage: 20,
  mortgageType: 'fixed' as const,
  loanProgram: 'conventional' as const,
  amortizationPeriod: 30,
  startDate: START_DATE as unknown as Date,
  paymentFrequency: 'monthly' as const,
  pmi: { type: 'percent' as const, value: 0 },
};

export const M500K_30_550_TAB_TITLE = '30-Year Fixed 5.50%';
export const M500K_30_650_TAB_TITLE = '30-Year Fixed 6.50%';
export const M500K_30_750_TAB_TITLE = '30-Year Fixed 7.50%';

const m500k30_550: ShareableTab = {
  id: '500k-30-550',
  title: M500K_30_550_TAB_TITLE,
  mortgageTerms: { ...m500kLoanBaseTerms, rate: 5.5 },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

const m500k30_650: ShareableTab = {
  id: '500k-30-650',
  title: M500K_30_650_TAB_TITLE,
  mortgageTerms: { ...m500kLoanBaseTerms, rate: 6.5 },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

const m500k30_750: ShareableTab = {
  id: '500k-30-750',
  title: M500K_30_750_TAB_TITLE,
  mortgageTerms: { ...m500kLoanBaseTerms, rate: 7.5 },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

export const M500K_MORTGAGE_MONTHLY_PAYMENT_URL = buildScenarioUrl({
  version: 1,
  activeTabId: 'compare',
  tabs: [m500k30_550, m500k30_650, m500k30_750],
});

// ---------------------------------------------------------------------------
// Article: $400,000 mortgage monthly payment by rate (US)
// $400k loan modeled as 20% down on $500k purchase, conventional fixed, monthly P&I
// Three tabs: 30-year at 5.50%, 6.50%, 7.50%; CTA opens 6.50% tab
// ---------------------------------------------------------------------------

const m400kLoanBaseTerms = {
  country: 'USA' as const,
  homePrice: 500_000,
  downPaymentAmount: 100_000,
  downPaymentPercentage: 20,
  mortgageType: 'fixed' as const,
  loanProgram: 'conventional' as const,
  amortizationPeriod: 30,
  startDate: START_DATE as unknown as Date,
  paymentFrequency: 'monthly' as const,
  pmi: { type: 'percent' as const, value: 0 },
};

export const M400K_30_550_TAB_TITLE = '30-Year Fixed 5.50%';
export const M400K_30_650_TAB_TITLE = '30-Year Fixed 6.50%';
export const M400K_30_750_TAB_TITLE = '30-Year Fixed 7.50%';

const m400k30_550: ShareableTab = {
  id: '400k-30-550',
  title: M400K_30_550_TAB_TITLE,
  mortgageTerms: { ...m400kLoanBaseTerms, rate: 5.5 },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

const m400k30_650: ShareableTab = {
  id: '400k-30-650',
  title: M400K_30_650_TAB_TITLE,
  mortgageTerms: { ...m400kLoanBaseTerms, rate: 6.5 },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

const m400k30_750: ShareableTab = {
  id: '400k-30-750',
  title: M400K_30_750_TAB_TITLE,
  mortgageTerms: { ...m400kLoanBaseTerms, rate: 7.5 },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

export const M400K_MORTGAGE_MONTHLY_PAYMENT_URL = buildScenarioUrl({
  version: 1,
  activeTabId: '400k-30-650',
  tabs: [m400k30_550, m400k30_650, m400k30_750],
});

// ---------------------------------------------------------------------------
// Article: Canadian mortgage break penalty and IRD calculation
// $625k purchase, 20% down ($500k mortgage), 25-year amortization
// Tab A: Keep at 5.50% fixed | Tab B: Refinance to 4.00% fixed
// Illustrates the rate savings a borrower must weigh against the IRD penalty
// ---------------------------------------------------------------------------

export const BREAK_PENALTY_IRD_KEEP_TAB_TITLE = 'Keep: 5.50% fixed';
export const BREAK_PENALTY_IRD_REFI_TAB_TITLE = 'Refinance: 4.00% fixed';

const irdKeep550: ShareableTab = {
  id: 'ird-keep-550',
  title: BREAK_PENALTY_IRD_KEEP_TAB_TITLE,
  mortgageTerms: {
    country: 'Canada',
    homePrice: 625000,
    downPaymentAmount: 125000,
    downPaymentPercentage: 20,
    mortgageType: 'fixed',
    loanProgram: 'conventional',
    rate: 5.50,
    amortizationPeriod: 25,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0 },
    canadianTermDetails: { termLength: 5, showRenewalScenario: false },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

const irdRefi400: ShareableTab = {
  id: 'ird-refi-400',
  title: BREAK_PENALTY_IRD_REFI_TAB_TITLE,
  mortgageTerms: {
    ...irdKeep550.mortgageTerms,
    rate: 4.00,
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

export const CANADIAN_MORTGAGE_BREAK_PENALTY_IRD_URL = buildScenarioUrl({
  version: 1,
  activeTabId: 'compare',
  tabs: [irdKeep550, irdRefi400],
});

// ---------------------------------------------------------------------------
// Article: 30-year amortization in Canada - who qualifies and what the extra 5 years costs
// $600k purchase, 5% down ($30k), 5.20% fixed, CMHC insured
// Tab A: 25-year amortization | Tab B: 30-year amortization
// Illustrates the monthly savings vs total interest cost of the extended amortization
// ---------------------------------------------------------------------------

export const AMORT_30YR_CA_25_TAB_TITLE = '25-year amortization';
export const AMORT_30YR_CA_30_TAB_TITLE = '30-year amortization';

const amort30yrCa25: ShareableTab = {
  id: 'amort-ca-25yr',
  title: AMORT_30YR_CA_25_TAB_TITLE,
  mortgageTerms: {
    country: 'Canada',
    homePrice: 600000,
    downPaymentAmount: 30000,
    downPaymentPercentage: 5,
    mortgageType: 'fixed',
    loanProgram: 'cmhc',
    rate: 5.20,
    amortizationPeriod: 25,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0 },
    canadianTermDetails: { termLength: 5, showRenewalScenario: false },
    cmhcDetails: { premiumRate: 0.04, province: 'ON', includeProvincialTax: false },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

const amort30yrCa30: ShareableTab = {
  id: 'amort-ca-30yr',
  title: AMORT_30YR_CA_30_TAB_TITLE,
  mortgageTerms: {
    ...amort30yrCa25.mortgageTerms,
    amortizationPeriod: 30,
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

export const AMORT_30YR_CANADA_URL = buildScenarioUrl({
  version: 1,
  activeTabId: 'compare',
  tabs: [amort30yrCa25, amort30yrCa30],
});

// ---------------------------------------------------------------------------
// Article: Closing costs in the US - what to budget beyond your down payment
// $400,000 purchase, 10% down ($40,000), 6.50% fixed, 30 years, conventional
// Single tab illustrating P&I on the loan; closing cost itemization in article prose
// ---------------------------------------------------------------------------

export const CLOSING_COSTS_US_CONV_TAB_TITLE = 'Conventional $400k, 10% down';

const closingCostsUsConv: ShareableTab = {
  id: 'closing-costs-us-conv-400k',
  title: CLOSING_COSTS_US_CONV_TAB_TITLE,
  mortgageTerms: {
    country: 'USA',
    homePrice: 400000,
    downPaymentAmount: 40000,
    downPaymentPercentage: 10,
    mortgageType: 'fixed',
    loanProgram: 'conventional',
    rate: 6.5,
    amortizationPeriod: 30,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0.8 },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

export const CLOSING_COSTS_US_EXPLAINED_URL = buildScenarioUrl({
  version: 1,
  activeTabId: 'closing-costs-us-conv-400k',
  tabs: [closingCostsUsConv],
});

// ---------------------------------------------------------------------------
// Article: Canadian mortgage renewal guide - posted rate vs broker quote
// $550k purchase, 20% down ($440k mortgage), 25-year amort, 5-year term
// Original rate 3.49% fixed; balance at renewal ~$380k
// Tab A: renew at posted 6.10% | Tab B: renew at broker 5.65%
// ---------------------------------------------------------------------------

export const CA_RENEWAL_GUIDE_POSTED_TAB_TITLE = 'Renew at posted 6.10%';
export const CA_RENEWAL_GUIDE_BROKER_TAB_TITLE = 'Renew at broker 5.65%';

const caRenewalGuideBase: ShareableTab = {
  id: 'ca-renewal-posted',
  title: CA_RENEWAL_GUIDE_POSTED_TAB_TITLE,
  mortgageTerms: {
    country: 'Canada',
    homePrice: 550000,
    downPaymentAmount: 110000,
    downPaymentPercentage: 20,
    mortgageType: 'fixed',
    loanProgram: 'conventional',
    rate: 3.49,
    amortizationPeriod: 25,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0 },
    canadianTermDetails: {
      termLength: 5,
      showRenewalScenario: true,
      renewalRate: 6.10,
    },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

const caRenewalGuideBroker: ShareableTab = {
  id: 'ca-renewal-broker',
  title: CA_RENEWAL_GUIDE_BROKER_TAB_TITLE,
  mortgageTerms: {
    ...caRenewalGuideBase.mortgageTerms,
    canadianTermDetails: {
      termLength: 5,
      showRenewalScenario: true,
      renewalRate: 5.65,
    },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

export const CANADIAN_MORTGAGE_RENEWAL_GUIDE_URL = buildScenarioUrl({
  version: 1,
  activeTabId: 'compare',
  tabs: [caRenewalGuideBase, caRenewalGuideBroker],
});

// ---------------------------------------------------------------------------
// Article: First-time home buyer mortgage in Canada - calculator inputs
// $650k Ontario purchase, 25-year amort, 5-year term, 5.10% fixed
// Tab A: stepped minimum down with CMHC | Tab B: 20% down (HBP + FHSA path)
// ---------------------------------------------------------------------------

export const FTHB_CA_MIN_DOWN_TAB_TITLE = 'Stepped minimum down (CMHC)';
export const FTHB_CA_20_DOWN_TAB_TITLE = '20% down (no CMHC)';

const FTHB_CA_ARTICLE_RATE = 5.1;

const fthbCaMinDown: ShareableTab = {
  id: 'fthb-ca-min-down',
  title: FTHB_CA_MIN_DOWN_TAB_TITLE,
  mortgageTerms: {
    country: 'Canada',
    homePrice: 650000,
    downPaymentAmount: 40000,
    downPaymentPercentage: 6.15,
    mortgageType: 'fixed',
    loanProgram: 'cmhc',
    rate: FTHB_CA_ARTICLE_RATE,
    amortizationPeriod: 25,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0 },
    canadianTermDetails: { termLength: 5, showRenewalScenario: false },
    cmhcDetails: { premiumRate: 0.04, province: 'ON', includeProvincialTax: false },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

const fthbCa20Down: ShareableTab = {
  id: 'fthb-ca-20-down',
  title: FTHB_CA_20_DOWN_TAB_TITLE,
  mortgageTerms: {
    country: 'Canada',
    homePrice: 650000,
    downPaymentAmount: 130000,
    downPaymentPercentage: 20,
    mortgageType: 'fixed',
    loanProgram: 'conventional',
    rate: FTHB_CA_ARTICLE_RATE,
    amortizationPeriod: 25,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0 },
    canadianTermDetails: { termLength: 5, showRenewalScenario: false },
  },
  annualExpenses: ZERO_EXPENSES,
  extraPayments: NO_EXTRA_PAYMENTS,
};

export const FTHB_CALCULATOR_CA_URL = buildScenarioUrl({
  version: 1,
  activeTabId: 'compare',
  tabs: [fthbCaMinDown, fthbCa20Down],
});

// ---------------------------------------------------------------------------
// Article: Ontario mortgage guide - CMHC, land transfer tax, affordability
// Tab A: Toronto $900k, 20% down, 5.20% fixed, 25-year (conventional)
// Tab B: Ottawa $650k, 10% down, 5.20% fixed, CMHC insured
// ---------------------------------------------------------------------------

export const ON_GUIDE_TORONTO_TAB_TITLE = 'Toronto $900k, 20% down';
export const ON_GUIDE_OTTAWA_TAB_TITLE = 'Ottawa $650k, 10% down';

const ON_GUIDE_ARTICLE_RATE = 5.2;

const onGuideExpensesToronto: AnnualExpensesData = {
  propertyTaxes: { type: 'percent', value: 0.6 },
  homeInsurance: { type: 'amount', value: 175 },
  hoaUtilities: { type: 'amount', value: 0 },
};

const onGuideExpensesOttawa: AnnualExpensesData = {
  propertyTaxes: { type: 'percent', value: 1.0 },
  homeInsurance: { type: 'amount', value: 175 },
  hoaUtilities: { type: 'amount', value: 0 },
};

const onGuideToronto900k: ShareableTab = {
  id: 'on-guide-toronto-900k',
  title: ON_GUIDE_TORONTO_TAB_TITLE,
  mortgageTerms: {
    country: 'Canada',
    homePrice: 900000,
    downPaymentAmount: 180000,
    downPaymentPercentage: 20,
    mortgageType: 'fixed',
    loanProgram: 'conventional',
    rate: ON_GUIDE_ARTICLE_RATE,
    amortizationPeriod: 25,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0 },
    canadianTermDetails: { termLength: 5, showRenewalScenario: false },
  },
  annualExpenses: onGuideExpensesToronto,
  extraPayments: NO_EXTRA_PAYMENTS,
};

const onGuideOttawa650k: ShareableTab = {
  id: 'on-guide-ottawa-650k',
  title: ON_GUIDE_OTTAWA_TAB_TITLE,
  mortgageTerms: {
    country: 'Canada',
    homePrice: 650000,
    downPaymentAmount: 65000,
    downPaymentPercentage: 10,
    mortgageType: 'fixed',
    loanProgram: 'cmhc',
    rate: ON_GUIDE_ARTICLE_RATE,
    amortizationPeriod: 25,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0 },
    canadianTermDetails: { termLength: 5, showRenewalScenario: false },
    cmhcDetails: { premiumRate: 0.031, province: 'ON', includeProvincialTax: false },
  },
  annualExpenses: onGuideExpensesOttawa,
  extraPayments: NO_EXTRA_PAYMENTS,
};

export const MORTGAGE_CALCULATOR_ONTARIO_GUIDE_URL = buildScenarioUrl({
  version: 1,
  activeTabId: 'compare',
  tabs: [onGuideToronto900k, onGuideOttawa650k],
});

export const MORTGAGE_CALCULATOR_ONTARIO_GUIDE_STATE: ShareState = {
  version: 1,
  activeTabId: onGuideToronto900k.id,
  tabs: [onGuideToronto900k, onGuideOttawa650k],
};

/** Per-tab flags for province calculator closing-cost UI (Toronto dual LTT vs provincial only). */
export const ONTARIO_GUIDE_TAB_IS_TORONTO: Record<string, boolean> = {
  [onGuideToronto900k.id]: true,
  [onGuideOttawa650k.id]: false,
};

// ---------------------------------------------------------------------------
// Article: BC mortgage guide - PTT, exemptions, CMHC, closing costs
// Tab A: Vancouver $850k, 10% down, 5.20% fixed, CMHC insured
// Tab B: Victoria $650k, 10% down, 5.20% fixed, CMHC insured
// ---------------------------------------------------------------------------

export const BC_GUIDE_VANCOUVER_TAB_TITLE = 'Vancouver $850k, 10% down';
export const BC_GUIDE_VICTORIA_TAB_TITLE = 'Victoria $650k, 10% down';

const BC_GUIDE_ARTICLE_RATE = 5.2;

const bcGuideExpensesVancouver: AnnualExpensesData = {
  propertyTaxes: { type: 'percent', value: 0.3 },
  homeInsurance: { type: 'amount', value: 150 },
  hoaUtilities: { type: 'amount', value: 0 },
};

const bcGuideExpensesVictoria: AnnualExpensesData = {
  propertyTaxes: { type: 'percent', value: 0.5 },
  homeInsurance: { type: 'amount', value: 150 },
  hoaUtilities: { type: 'amount', value: 0 },
};

const bcGuideVancouver850k: ShareableTab = {
  id: 'bc-guide-vancouver-850k',
  title: BC_GUIDE_VANCOUVER_TAB_TITLE,
  mortgageTerms: {
    country: 'Canada',
    homePrice: 850000,
    downPaymentAmount: 85000,
    downPaymentPercentage: 10,
    mortgageType: 'fixed',
    loanProgram: 'cmhc',
    rate: BC_GUIDE_ARTICLE_RATE,
    amortizationPeriod: 25,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0 },
    canadianTermDetails: { termLength: 5, showRenewalScenario: false },
    cmhcDetails: { premiumRate: 0.031, province: 'BC', includeProvincialTax: false },
  },
  annualExpenses: bcGuideExpensesVancouver,
  extraPayments: NO_EXTRA_PAYMENTS,
};

const bcGuideVictoria650k: ShareableTab = {
  id: 'bc-guide-victoria-650k',
  title: BC_GUIDE_VICTORIA_TAB_TITLE,
  mortgageTerms: {
    country: 'Canada',
    homePrice: 650000,
    downPaymentAmount: 65000,
    downPaymentPercentage: 10,
    mortgageType: 'fixed',
    loanProgram: 'cmhc',
    rate: BC_GUIDE_ARTICLE_RATE,
    amortizationPeriod: 25,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0 },
    canadianTermDetails: { termLength: 5, showRenewalScenario: false },
    cmhcDetails: { premiumRate: 0.031, province: 'BC', includeProvincialTax: false },
  },
  annualExpenses: bcGuideExpensesVictoria,
  extraPayments: NO_EXTRA_PAYMENTS,
};

export const MORTGAGE_CALCULATOR_BC_GUIDE_URL = buildScenarioUrl({
  version: 1,
  activeTabId: 'compare',
  tabs: [bcGuideVancouver850k, bcGuideVictoria650k],
});

export const MORTGAGE_CALCULATOR_BC_GUIDE_STATE: ShareState = {
  version: 1,
  activeTabId: bcGuideVancouver850k.id,
  tabs: [bcGuideVancouver850k, bcGuideVictoria650k],
};

// ---------------------------------------------------------------------------
// Article: Alberta mortgage guide - no LTT, land title fee, Calgary vs Edmonton
// Tab A: Calgary $700k, 10% down, 5.20% fixed, CMHC insured
// Tab B: Edmonton $550k, 10% down, 5.20% fixed, CMHC insured
// ---------------------------------------------------------------------------

export const AB_GUIDE_CALGARY_TAB_TITLE = 'Calgary $700k, 10% down';
export const AB_GUIDE_EDMONTON_TAB_TITLE = 'Edmonton $550k, 10% down';

const AB_GUIDE_ARTICLE_RATE = 5.2;

const abGuideExpensesCalgary: AnnualExpensesData = {
  propertyTaxes: { type: 'percent', value: 0.65 },
  homeInsurance: { type: 'amount', value: 150 },
  hoaUtilities: { type: 'amount', value: 0 },
};

const abGuideExpensesEdmonton: AnnualExpensesData = {
  propertyTaxes: { type: 'percent', value: 0.7 },
  homeInsurance: { type: 'amount', value: 150 },
  hoaUtilities: { type: 'amount', value: 0 },
};

const abGuideCalgary700k: ShareableTab = {
  id: 'ab-guide-calgary-700k',
  title: AB_GUIDE_CALGARY_TAB_TITLE,
  mortgageTerms: {
    country: 'Canada',
    homePrice: 700000,
    downPaymentAmount: 70000,
    downPaymentPercentage: 10,
    mortgageType: 'fixed',
    loanProgram: 'cmhc',
    rate: AB_GUIDE_ARTICLE_RATE,
    amortizationPeriod: 25,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0 },
    canadianTermDetails: { termLength: 5, showRenewalScenario: false },
    cmhcDetails: { premiumRate: 0.031, province: 'Other', includeProvincialTax: false },
  },
  annualExpenses: abGuideExpensesCalgary,
  extraPayments: NO_EXTRA_PAYMENTS,
};

const abGuideEdmonton550k: ShareableTab = {
  id: 'ab-guide-edmonton-550k',
  title: AB_GUIDE_EDMONTON_TAB_TITLE,
  mortgageTerms: {
    country: 'Canada',
    homePrice: 550000,
    downPaymentAmount: 55000,
    downPaymentPercentage: 10,
    mortgageType: 'fixed',
    loanProgram: 'cmhc',
    rate: AB_GUIDE_ARTICLE_RATE,
    amortizationPeriod: 25,
    startDate: START_DATE as unknown as Date,
    paymentFrequency: 'monthly',
    pmi: { type: 'percent', value: 0 },
    canadianTermDetails: { termLength: 5, showRenewalScenario: false },
    cmhcDetails: { premiumRate: 0.031, province: 'Other', includeProvincialTax: false },
  },
  annualExpenses: abGuideExpensesEdmonton,
  extraPayments: NO_EXTRA_PAYMENTS,
};

export const MORTGAGE_CALCULATOR_ALBERTA_GUIDE_URL = buildScenarioUrl({
  version: 1,
  activeTabId: 'compare',
  tabs: [abGuideCalgary700k, abGuideEdmonton550k],
});

export const MORTGAGE_CALCULATOR_ALBERTA_GUIDE_STATE: ShareState = {
  version: 1,
  activeTabId: abGuideCalgary700k.id,
  tabs: [abGuideCalgary700k, abGuideEdmonton550k],
};

// --- APPEND NEW ARTICLE SCENARIOS ABOVE THIS LINE --------------------------------
